import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { OnoffType } from '@/api/enum/common.enum';
import {
	MeditationPageDTO,
	MeditationPageFilter,
} from '@/api/DTO/exercise/meditation.interface';
import { showLabel } from '@/utils/helper';
import MeditationListSummaryFilterSection from './components/BodyListSummaryFilterSection';
import SetMeditationStatusDialog from './components/SetBodyStatusDialog';
import SetMeditationOrderDialog from './components/SetBodyOrderDialog';
import { useGetBodyListQuery } from '@/api/exercise/bodyApiSlice';
import SetBodyNewDialog from './components/SetBodyNewDialog';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
	id?: string;
	published?: number | string | boolean;
}

export default function BodyListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();

	const statusList = [
		{ id: OnoffType.off, name: t('meditation.off') },
		{ id: OnoffType.on, name: t('meditation.on') },
	];

	const defaultPropertyFilter: MeditationPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		statuses: [],
	};
	const defaultValues: DefaultValues<MeditationPageFilter> =
		defaultPropertyFilter;
	const methods = useForm<MeditationPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: MeditationPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'statuses',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBodyListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: MeditationPageDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MeditationPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('meditation.seriesTitleZh'),
			flex: 25,
		},
		{
			field: 'singleCount',
			headerName: t('meditation.exerciseCount'),
			flex: 25,
		},
		{
			field: 'singleHasNewCount',
			headerName: t('meditation.newExerciseCount'),
			flex: 25,
		},
		{
			field: 'status',
			headerName: t('meditation.seriesStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.status, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('meditation.lastUpdateTime'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('meditation.createTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/exercise-manage/body-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.body')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetBodyNewDialog />
					<SetMeditationStatusDialog />
					<SetMeditationOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/exercise-manage/body-list/add`)}
					>
						{t('meditation.createSeries')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.seriesId}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
